import * as React from 'react';

import { addDays, addMinutes, format, isBefore, isEqual, parse, startOfDay } from 'date-fns';
import { useLocale } from '../../lib/hooks/use-locale.ts';
import { formatISOTimeForDisplay } from '../../utils/time-utils.ts';
import { Button } from './button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectTriggerNoStyling,
    SelectValue,
} from './select.tsx';

export type TimePickerProps = {
    /**
     * The current value as a time string in HH:mm:ss format
     */
    value: string | null;
    /**
     * Callback when value changes, providing the new time string in HH:mm:ss format
     */
    onChange?: (value: string) => void;
    /**
     * Placeholder for the select input
     */
    placeholder?: string;
    /**
     * Whether the component is disabled
     */
    disabled?: boolean;
    /**
     * Additional class name for styling
     */
    className?: string;
    /**
     * The minimum time to display
     */
    min?: string;
    /**
     * The maximum time to display
     */
    max?: string;
};

/**
 * Generate time options in 30-minute intervals for a full day
 */
const generateTimeOptions = (startTime?: string, endTime?: string): string[] => {
    const start = startTime ? parse(startTime, 'HH:mm:ss', new Date()) : startOfDay(new Date());
    const end = endTime ? parse(endTime, 'HH:mm:ss', new Date()) : startOfDay(addDays(new Date(), 1));

    let current = start;
    const options: string[] = [];

    while (isBefore(current, end) || isEqual(current, end)) {
        const option = format(current, 'HH:mm:ss');

        if (!options.includes(option)) {
            options.push(option);
        }

        current = addMinutes(current, 15);
    }

    return options;
};

const PREFERRED_TIME_OPTION = '08:00:00';

/**
 * TimePicker component for selecting time values
 */
export const TimePicker: React.FC<TimePickerProps> = ({
    value,
    onChange,
    placeholder,
    disabled = false,
    className,
    min,
    max,
}) => {
    const { t } = useLocale('common');
    const [preferredOptionElement, setPreferredOptionElement] = React.useState<HTMLDivElement | null>(null);
    const options = React.useMemo(() => generateTimeOptions(min, max), [min, max]);

    React.useEffect(() => {
        if (preferredOptionElement && !value) {
            setTimeout(() => {
                if (document.body.contains(preferredOptionElement)) {
                    preferredOptionElement.focus();
                }
            });
        }
    }, [preferredOptionElement, value]);

    return (
        <Select
            value={value ?? ''}
            onValueChange={(newValue) => {
                if (onChange && newValue) {
                    onChange(newValue);
                }
            }}
            disabled={disabled}
        >
            <SelectTriggerNoStyling className={className} asChild>
                <Button variant="ghost" className="shrink w-auto">
                    <SelectValue placeholder={placeholder ?? t('time_picker__placeholder')} />
                </Button>
            </SelectTriggerNoStyling>
            <SelectContent>
                {options.map((time) => (
                    <SelectItem
                        key={time}
                        value={time}
                        ref={time === PREFERRED_TIME_OPTION ? setPreferredOptionElement : undefined}
                    >
                        {t('time_picker__time_option', { time: formatISOTimeForDisplay(time, 'HH:mm') })}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
};

export default TimePicker;
